/**
 * @file うんどうチェック — 本の遊び単位で ◎○△× を日付ごとに残すカード (うんどうタブ)。
 *
 * @what: 『0〜5歳児の…運動あそび』の遊び (週メニューに載せたもの + 2 歳の親子あそび全 41 件) を
 *   考査のテーマ別に、大きめのページサムネイルつきの**カードの格子**で並べる。年齢のチェック
 *   (2歳 / 3〜4歳 / 4〜5歳、初期は全部オン) を外すと絞り込める。◎○△× は同じ記号の再タップで
 *   取り消し、別の記号で付け替え。サムネイルのタップで全画面に拡大、p.NNN で本だなの該当ページへ。
 *   下は遊び × 直近のチェック日のマトリクス (記録のある遊び・日付だけ、幅は中身なり)。
 *   マトリクスの日付・記号を押すとその日のチェックに切り替わり、カードで付け替え・取り消しが
 *   できる (あとから直す動線)。記録の実体は StudyProgressLog (motor_item_id + rating)。
 * @why: 1 列の表は縦に長すぎ、サムネイルも小さくて見えなかった (代表 2026-08-24)。カード型 +
 *   複数列 + 大きいサムネイル + 年齢の絞り込みで「今日やる遊びを選ぶ」動線にする。
 * @failure: 追加・削除の失敗は actionError の 1 行 (詳細は console)。読み込み中は操作を無効にする。
 *   本だなに資料が無ければサムネイル・ページリンクは出ない (チェックは使える)。
 */

import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { formatLogDate, todayLocal } from "../api/dates";
import type { StudyPage } from "../api/pages";
import {
  isStudyMotorRating,
  newProgressLogId,
  STUDY_MOTOR_RATING_LABELS,
  STUDY_MOTOR_RATING_MARKS,
  STUDY_MOTOR_RATINGS,
  type StudyMotorRating,
  type StudyProgressLog,
  type StudyProgressLogInput,
} from "../api/plans";
import type { ProgressLogsState } from "../hooks/useProgressLogs";
import { useSourcePageMap } from "../hooks/useSourcePageMap";
import {
  MOTOR_GROUPS,
  MOTOR_ITEMS,
  type MotorItem,
  motorItemLabel,
  motorItemPage,
  UNDOU_ASOBI_SOURCE_ID,
} from "../model/motorItems";
import { LazyPageImage, SourcePageLightbox } from "./pageImages";
import "./study.css";

/** 年齢の絞り込みの選択肢 (カタログの age の語彙と同じ)。 */
const AGES = ["2歳", "3〜4歳", "4〜5歳"] as const;

/** その (遊び, 日付) の記録。同じ組が複数あれば新しいもの (一覧は新しい順) を採る。 */
function checkFor(
  logs: readonly StudyProgressLog[],
  itemId: string,
  date: string,
): StudyProgressLog | null {
  return logs.find((l) => l.motor_item_id === itemId && l.date === date) ?? null;
}

export function MotorCheckCard({
  logsState,
  onAdd,
  onRemove,
  busy,
  actionError,
  pageHref,
  familyId,
  favorites,
  onToggleFavorite,
}: {
  logsState: ProgressLogsState;
  onAdd: (input: StudyProgressLogInput) => Promise<StudyProgressLog | null>;
  onRemove: (id: string) => Promise<void>;
  busy: boolean;
  actionError: string | null;
  /** 本のページ番号 → 本だなのリーダーへの href。本だなに資料が無ければ渡さない。 */
  pageHref?: (page: number) => string;
  /** 本だなのページ一覧を引く鍵。サムネイル表示に使う (無ければサムネイルなし)。 */
  familyId: string;
  /** お気に入りの遊び id (plan の draft。★ を押すと保存バーが出て、押した順に並ぶ)。 */
  favorites: readonly string[];
  onToggleFavorite: (itemId: string) => void;
}) {
  const [date, setDate] = useState(todayLocal());
  const [ages, setAges] = useState<ReadonlySet<string>>(new Set(AGES));
  // サムネイル: 本だなに資料があるとき (pageHref が来るとき) だけページ一覧を引く
  const pageMap = useSourcePageMap(familyId, pageHref ? UNDOU_ASOBI_SOURCE_ID : null);
  const [enlarged, setEnlarged] = useState<{ page: StudyPage; label: string } | null>(null);
  // 未知の rating (将来の値・壊れたデータ) は表示から落とす (記号表を undefined で壊さない)。
  // カード約 100 枚の描画の元になるので、記録が変わらない限り作り直さない
  const motorLogs = useMemo(
    () =>
      logsState.status === "ready"
        ? logsState.items.filter((l) => l.motor_item_id && isStudyMotorRating(l.rating))
        : [],
    [logsState],
  );

  const toggleAge = (age: string) => {
    setAges((prev) => {
      const next = new Set(prev);
      if (next.has(age)) next.delete(age);
      else next.add(age);
      return next;
    });
  };
  // 年齢の無い項目 (連続運動のコース化) は絞り込みに関わらず出す
  const visible = (age: string | undefined) => age === undefined || ages.has(age);

  const tap = async (itemId: string, rating: StudyMotorRating) => {
    const current = checkFor(motorLogs, itemId, date);
    if (current) {
      await onRemove(current.id);
      if (current.rating === rating) return; // 同じ記号 = 取り消しで終わり
    }
    await onAdd({ id: newProgressLogId(), date, motor_item_id: itemId, rating });
  };

  // 履歴: 記録のある日付 (新しい順、8 列まで) × 記録のある遊び (カタログ順 + カタログ外の id)
  const dates = useMemo(
    () => [...new Set(motorLogs.map((l) => l.date))].sort().reverse().slice(0, 8),
    [motorLogs],
  );
  const rowIds = useMemo(
    () => [
      ...MOTOR_ITEMS.filter((i) => motorLogs.some((l) => l.motor_item_id === i.id)).map((i) => i.id),
      ...[...new Set(motorLogs.map((l) => l.motor_item_id ?? ""))].filter(
        (id) => id && !MOTOR_ITEMS.some((i) => i.id === id),
      ),
    ],
    [motorLogs],
  );

  // お気に入り (plan の motor_favorites) は画面では「よくやるメニュー」と呼ぶ。
  // 年齢の絞り込みに関わらず出す (すぐ選ぶための場所なので隠さない)。カタログに無い id
  // (改版で消えた遊び) は表示から落とす — plan 側には残るので、戻れば また出る
  const favoriteItems = MOTOR_ITEMS.filter((i) => favorites.includes(i.id));

  const renderCard = (item: MotorItem) => {
    const current = checkFor(motorLogs, item.id, date);
    const thumbPage = item.page !== undefined ? pageMap?.[item.page] : undefined;
    const isFavorite = favorites.includes(item.id);
    return (
      <li key={item.id} className="study-motor-card">
        {thumbPage ? (
          <LazyPageImage
            page={thumbPage}
            label={`${item.label}のページ`}
            className="study-motor-card-thumb"
            onOpen={() => setEnlarged({ page: thumbPage, label: item.label })}
          />
        ) : (
          <div className="study-motor-card-thumb study-motor-card-thumb--empty" aria-hidden="true" />
        )}
        <div className="study-motor-card-label">
          <span className="study-motor-item-label">{item.label}</span>
          <span className="study-motor-card-sub">
            {item.age ? <span className="study-motor-age">{item.age}</span> : null}
            {item.page !== undefined && pageHref ? (
              <Link
                className="study-motor-page"
                to={pageHref(item.page)}
                title="本だなで、この遊びのページを開く"
              >
                p.{item.page}
              </Link>
            ) : null}
          </span>
        </div>
        <button
          type="button"
          className={`study-motor-fav${isFavorite ? " study-motor-fav--on" : ""}`}
          aria-pressed={isFavorite}
          aria-label={`${item.label}を${isFavorite ? "よくやるメニューから外す" : "よくやるメニューに入れる"}`}
          title={isFavorite ? "よくやるメニューから外す" : "よくやるメニューに入れる"}
          onClick={() => onToggleFavorite(item.id)}
        >
          {isFavorite ? "★" : "☆"}
        </button>
        <span className="study-motor-btns" role="group" aria-label={`${item.label}のチェック`}>
          {STUDY_MOTOR_RATINGS.map((r) => (
            <button
              key={r}
              type="button"
              className={`study-motor-btn study-motor-btn--${r}${
                current?.rating === r ? " study-motor-btn--on" : ""
              }`}
              aria-pressed={current?.rating === r}
              title={`${STUDY_MOTOR_RATING_LABELS[r]}${
                current?.rating === r ? "（もう一度押すと取り消し）" : ""
              }`}
              disabled={busy || logsState.status !== "ready"}
              onClick={() => void tap(item.id, r)}
            >
              {STUDY_MOTOR_RATING_MARKS[r]}
            </button>
          ))}
        </span>
      </li>
    );
  };

  return (
    <div className="study-motor">
      <div className="study-motor-legend">
        {STUDY_MOTOR_RATINGS.map((r) => (
          <span key={r}>
            <strong>{STUDY_MOTOR_RATING_MARKS[r]}</strong> {STUDY_MOTOR_RATING_LABELS[r]}
          </span>
        ))}
      </div>
      <div className="study-motor-date">
        <label>
          <span>チェックする日</span>
          <input
            type="date"
            value={date}
            max={todayLocal()}
            aria-label="チェックする日"
            onChange={(e) => setDate(e.target.value || todayLocal())}
          />
        </label>
        <div className="study-motor-ages" role="group" aria-label="年齢で絞り込む">
          {AGES.map((age) => (
            <label key={age} className="study-motor-age-check">
              <input type="checkbox" checked={ages.has(age)} onChange={() => toggleAge(age)} />
              <span>{age}</span>
            </label>
          ))}
        </div>
        {logsState.status === "loading" ? (
          <span role="status" className="study-muted">
            読み込み中...
          </span>
        ) : null}
      </div>

      {favoriteItems.length > 0 ? (
        <section className="study-motor-group study-motor-group--fav" aria-label="よくやるメニュー">
          <h4 className="study-motor-group-title">
            よくやるメニュー
            <span className="study-motor-item-force">★ を押した遊び</span>
          </h4>
          <ul className="study-motor-cards">{favoriteItems.map(renderCard)}</ul>
        </section>
      ) : null}

      {MOTOR_GROUPS.map((g) => {
        const items = g.items.filter((i) => visible(i.age));
        if (items.length === 0) return null;
        return (
          <section key={g.id} className="study-motor-group" aria-label={g.label}>
            <h4 className="study-motor-group-title">
              {g.label}
              <span className="study-motor-item-force">{g.force}</span>
            </h4>
            <ul className="study-motor-cards">{items.map(renderCard)}</ul>
          </section>
        );
      })}
      {actionError ? (
        <p role="alert" className="study-error">
          {actionError}
        </p>
      ) : null}
      {enlarged ? (
        <SourcePageLightbox
          page={enlarged.page}
          label={enlarged.label}
          onClose={() => setEnlarged(null)}
        />
      ) : null}

      {dates.length > 0 ? (
        <div className="study-motor-history">
          <div className="study-reader-review-title">これまでのチェック</div>
          <p className="study-section-help">
            日付や記号を押すと、その日のチェックに切り替わります。上のカードで付け替え・取り消しができます。
          </p>
          <div className="study-motor-history-scroll">
            <table className="study-motor-table">
              <thead>
                <tr>
                  <th scope="col">あそび</th>
                  {dates.map((d) => (
                    <th key={d} scope="col" {...(d === date ? { "aria-current": "date" as const } : {})}>
                      <button
                        type="button"
                        className="study-motor-history-date"
                        title="この日のチェックに切り替える"
                        aria-label={`${formatLogDate(d, false)} のチェックを直す`}
                        onClick={() => setDate(d)}
                      >
                        {formatLogDate(d, false)}
                      </button>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rowIds.map((itemId) => {
                  const page = motorItemPage(itemId);
                  return (
                    <tr key={itemId}>
                      <th scope="row">
                        {page !== undefined && pageHref ? (
                          <Link className="study-motor-page" to={pageHref(page)}>
                            {motorItemLabel(itemId)}
                          </Link>
                        ) : (
                          motorItemLabel(itemId)
                        )}
                      </th>
                      {dates.map((d) => {
                        const log = checkFor(motorLogs, itemId, d);
                        return (
                          <td
                            key={d}
                            title={log?.rating ? STUDY_MOTOR_RATING_LABELS[log.rating] : ""}
                            {...(d === date ? { "aria-current": "date" as const } : {})}
                          >
                            {log?.rating ? (
                              <button
                                type="button"
                                className="study-motor-history-mark"
                                aria-label={`${motorItemLabel(itemId)} の ${formatLogDate(d, false)} のチェックを直す`}
                                onClick={() => setDate(d)}
                              >
                                {STUDY_MOTOR_RATING_MARKS[log.rating]}
                              </button>
                            ) : (
                              ""
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}
    </div>
  );
}
